import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  OnInit,
  output,
  signal,
  ViewChild
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import {MatChipInputEvent, MatChipsModule} from '@angular/material/chips';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {FilterBlock, FilterField, FilterValue, FilterValueProperty, Operator} from '../../models/filter';
import {ScreeningCriteriaDTO} from '../../dto/screening-criteria';


const filters: FilterBlock[] = [
  {
    filter: FilterField.PDF_AVAILABLE,
    possibleOperators: [Operator.YES, Operator.NO],
    possibleValues: []
  },
  {
    filter: FilterField.SELF_CREATED,
    possibleOperators: [Operator.YES, Operator.NO],
    possibleValues: []
  },
  {
    filter: FilterField.TITLE_ABSTRACT_SCREENING,
    possibleOperators: [Operator.IS, Operator.IS_NOT],
    possibleValues: [FilterValue.AUDITED, FilterValue.PASSED],
  },
  {
    filter: FilterField.FULL_TEXT_SCREENING,
    possibleOperators: [Operator.IS, Operator.IS_NOT],
    possibleValues: [FilterValue.AUDITED, FilterValue.PASSED],
  },
  {
    filter: FilterField.MANUEL,
    possibleOperators: [Operator.YES, Operator.NO],
    possibleValues: [FilterValue.ACCEPTED, FilterValue.OVERWRITTEN],
  },
  {
    filter: FilterField.PROPERTY,
    possibleOperators: [Operator.IS_SET, Operator.IS_EMPTY],
    possibleValues: Object.values(FilterValueProperty).map(value => value.toString()),
  }
]

@Component({
  selector: 'app-research-paper-smart-search',
  imports: [MatFormFieldModule, MatChipsModule, MatIconModule, MatAutocompleteModule, FormsModule],
  templateUrl: './research-paper-smart-search.component.html',
  styleUrl: './research-paper-smart-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResearchPaperSmartSearchComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger) autocomplete?: MatAutocompleteTrigger;

  readonly screeningCriteria = input<ScreeningCriteriaDTO[]>();
  readonly changeFilter = output<FilterBlock[]>()
  readonly searchValue = output<string>()

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  readonly lastFilter = model<FilterBlock>();
  readonly currentInput = model('');
  readonly filters = signal<FilterBlock[]>([]);


  ngOnInit(): void {
    const criteriaNames = this.screeningCriteria()?.map(c => c.name).map(c => FilterValue.PASSED + " " + c) || [];
    const fullTextFilter = filters.find(f => f.filter === FilterField.FULL_TEXT_SCREENING);
    if (fullTextFilter) {
      fullTextFilter.possibleValues.push(...criteriaNames);
    }
    const titleFilter = filters.find(f => f.filter === FilterField.TITLE_ABSTRACT_SCREENING);
    if (titleFilter) {
      titleFilter.possibleValues.push(...criteriaNames);
    }
    if(this.autocomplete){
      this.autocomplete.closePanel();
    }
  }

  readonly filteredFilterBlocks = computed(() => {
    const currentInput = this.currentInput().toLowerCase();
    if (!this.lastFilter()) {
      return filters
        .map(filter => filter.filter)
        .filter(filter => filter.toLowerCase().includes(currentInput));
    }
    if (!this.lastFilter()?.operator) {
      if (currentInput.length === 0) {
        return this.lastFilter()?.possibleOperators;
      }
      return this.lastFilter()?.possibleOperators.filter(operator => operator.includes(currentInput));
    }
    if (currentInput.length === 0) {
      return this.lastFilter()?.possibleValues;
    }
    return this.lastFilter()?.possibleValues.filter(value => value.includes(currentInput));
  });


  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      if (this.addFilterEl(value)) {
        this.searchValue.emit(value);
        this.closePanel();
      }
    } else {
      this.searchValue.emit(value);
      this.closePanel();
    }
  }

  public closePanel(): void {
    if (this.autocomplete) {
      this.autocomplete.closePanel();
    }
  }

  removeLastFilter(): void {
    if (this.filters().length > 0) {
      this.lastFilter.set(this.filters()[this.filters().length - 1]);
      this.filters.update(filters => filters.slice(0, filters.length - 1));
      this.emitFilters();
    } else {
      this.lastFilter.set(undefined);
    }
  }

  removeLastOperator(): void {
    if (!this.lastFilter() && this.filters().length > 0) {
      this.lastFilter.set(this.filters()[this.filters().length - 1]);
      this.filters.update(filters => filters.slice(0, filters.length - 1));
      this.emitFilters();
    }

    if (this.lastFilter() && this.lastFilter()?.operator) {
      this.lastFilter.update(lastFilter => ({...lastFilter!, operator: undefined}));
    }
  }

  removeLastValue(): void {
    if (this.filters().length > 0) {
      this.lastFilter.set(this.filters()[this.filters().length - 1]);
      this.filters.update(filters => filters.slice(0, filters.length - 1));
      this.emitFilters();
    }
    if (this.lastFilter() && this.lastFilter()?.value) {
      this.lastFilter.update(lastFilter => ({...lastFilter!, value: undefined}));
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.addFilterEl(event.option.viewValue);
    event.option.deselect();
  }

  private addFilterEl(value: string): boolean {
    if (this.lastFilter()) {
      const lastFilter = this.lastFilter();
      if (lastFilter?.possibleOperators.includes(value as Operator)) {
        lastFilter!.operator = value as Operator;
        if (lastFilter?.possibleValues.length === 0) {
          this.filters.update(filters => [...filters, lastFilter]);
          this.lastFilter.set(undefined);
          this.currentInput.set('');
          this.emitFilters();
          return false;
        }
        this.lastFilter.update(() => ({...lastFilter}));
      }

      if (lastFilter?.operator && lastFilter?.possibleValues.includes(value)) {
        lastFilter.value = value;
        this.filters.update(filters => [...filters, lastFilter]);
        this.lastFilter.set(undefined);
        this.emitFilters();
      }
      this.currentInput.set('');
      return false;
    } else if (filters.filter(filter => filter.filter === value).length > 0) {
      const foundFilter = filters.filter(filter => filter.filter === value).at(0)!;
      this.lastFilter.update(() => foundFilter);
      this.currentInput.set('');
      return false
    }
    return true;
  }

  emitFilters(): void {
    this.changeFilter.emit(this.filters());
  }

  resetFilters(): void {
    this.currentInput.set('');
    this.lastFilter.set(undefined);
    this.filters.update(filters => []);
    this.emitFilters();
    this.searchValue.emit('')
  }

}
